import { FC, useRef, useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Box, Typography, Link as MuiLink } from '@mui/material'
import { useAppDispatch } from 'store/hooks'
import { closeModal } from 'store/modals/modalSlice'
import { categoryToUrl } from '../library/utils'
import ContentWrapper from '../../components/header/ContentWrapper'
import CategoryNavigation from './components/CategoryNavigation'

import MetaDataSection from './sections/meta-data/MetaDataSection'
import TopCategoryInsights from './sections/top-category-insights/TopCategoryInsights'
import LabelsSection from './sections/labels/LabelsSection'
import PerformanceSection from './sections/performance/PerformanceSection'
import BenchmarkingSection from './sections/benchmarking/BenchmarkingSection'
import CPIsSection from './sections/cpis/CPIsSection'

import useCategoryDashboardData from './useCategoryDashboardData'
import { CPIPeriod } from './categoryDashboard.types'
import {
  DashboardContainer,
  LeftColumn,
  RightColumn,
  RightTopRow,
  CategoryDashboardHeader
} from './categoryDashboard.styles'

const CategoryDashboard: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { categoryName: categoryId } = useParams<{ categoryName: string }>()
  const {
    data,
    allCategories,
    categoryName,
    isLoading,
    isBenchmarkingLoading,
    isRecentFindingsLoading,
    isPerformanceHistoryLoading,
    isBenchmarkHistoryLoading,
    isLabelsLoading,
    isAvailableCPIsLoading,
    onPerformanceTimePeriodChange,
    onBenchmarkTimePeriodChange,
    industryOptions,
    onIndustryChange,
    performanceCpis,
    benchmarkCpis,
    refetchActiveAndAvailable
  } = useCategoryDashboardData(categoryId)
  const [performancePeriod, setPerformancePeriod] = useState<CPIPeriod>(CPIPeriod.OneMonth)
  const [benchmarkingPeriod, setBenchmarkingPeriod] = useState<CPIPeriod>(CPIPeriod.OneMonth)
  const [cpisTab, setCpisTab] = useState<'active' | 'available'>('active')
  const pendingCpisTabReset = useRef(false)

  // Keep both period dropdowns in sync with the default 1M data that the hook
  // refetches whenever the category changes.
  useEffect(() => {
    setPerformancePeriod(CPIPeriod.OneMonth)
    setBenchmarkingPeriod(CPIPeriod.OneMonth)
    // Defer the CPIs-tab reset until after the new category's data lands so we
    // don't flash the Active tab during the transition.
    pendingCpisTabReset.current = true
  }, [categoryId])

  useEffect(() => {
    if (!isLoading && pendingCpisTabReset.current) {
      pendingCpisTabReset.current = false
      setCpisTab('active')
    }
  }, [isLoading])

  // Pin the URL to the title-derived slug. Old links and bookmarks address a category by its
  // backend id, which diverges from the title for Threat Detection and IAM. Keyed off the URL
  // param rather than the resolved categoryName, which lags a render behind the fetch.
  useEffect(() => {
    if (!categoryId) return
    const addressedById = allCategories.find((c) => c.id === categoryId)
    if (!addressedById) return
    const canonicalSlug = categoryToUrl(addressedById.title)
    if (canonicalSlug !== categoryId) {
      navigate(`/library/categories/${encodeURIComponent(canonicalSlug)}`, { replace: true })
    }
  }, [allCategories, categoryId, navigate])

  useEffect(() => {
    dispatch(closeModal())
    return () => {
      dispatch(closeModal())
    }
  }, [dispatch, categoryId])

  const handlePerformancePeriodChange = (period: CPIPeriod) => {
    setPerformancePeriod(period)
    onPerformanceTimePeriodChange(period)
  }

  const handleBenchmarkingPeriodChange = (period: CPIPeriod) => {
    setBenchmarkingPeriod(period)
    onBenchmarkTimePeriodChange(period)
  }

  const isEmptyState = data ? data.activeCPIs.length === 0 && data.labels.length === 0 : false
  const shouldStretch = isEmptyState

  const rightColumnRef = useRef<HTMLDivElement>(null)
  const [rightColumnHeight, setRightColumnHeight] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (!rightColumnRef.current) return
    const observer = new ResizeObserver(() => {
      if (rightColumnRef.current) {
        setRightColumnHeight(rightColumnRef.current.offsetHeight)
      }
    })
    observer.observe(rightColumnRef.current)
    return () => observer.disconnect()
  }, [data])

  return (
    <>
      <CategoryDashboardHeader>
        <Box className="header-content">
          <Box className="header-left">
            <Box className="breadcrumbs">
              <MuiLink component={Link} to="/" className="breadcrumb-link" underline="none">
                Home
              </MuiLink>
              <Typography className="breadcrumb-separator">/</Typography>
              <CategoryNavigation categories={allCategories} currentCategoryName={categoryName} />
            </Box>
            <Typography className="page-title">{categoryName || '\u00A0'}</Typography>
          </Box>
          <Box className="header-right" />
        </Box>
      </CategoryDashboardHeader>

      <ContentWrapper line={1} paddingBottom={0}>
        <DashboardContainer isEmpty={shouldStretch}>
          <LeftColumn sx={!isEmptyState && rightColumnHeight ? { height: rightColumnHeight } : undefined}>
            <MetaDataSection data={data?.metaData} loading={isLoading} />
            <TopCategoryInsights
              data={data?.insights}
              loading={isLoading}
              cpis={data?.activeCPIs.map((c) => ({ name: c.cpiName, label: c.cpiId })) ?? []}
            />
            <LabelsSection labels={data?.labels || []} loading={isLoading || isLabelsLoading} />
          </LeftColumn>
          <RightColumn ref={rightColumnRef}>
            <RightTopRow>
              <PerformanceSection
                data={data?.performance}
                selectedPeriod={performancePeriod}
                onTimePeriodChange={handlePerformancePeriodChange}
                performanceCpis={performanceCpis}
                loading={isLoading}
                historyLoading={isPerformanceHistoryLoading}
                hasActiveCpis={(data?.activeCPIs.length ?? 0) > 0}
              />
              <BenchmarkingSection
                data={data?.benchmarking}
                selectedPeriod={benchmarkingPeriod}
                onTimePeriodChange={handleBenchmarkingPeriodChange}
                industryOptions={industryOptions}
                onIndustryChange={onIndustryChange}
                benchmarkCpis={benchmarkCpis}
                loading={isLoading || isBenchmarkingLoading}
                historyLoading={isBenchmarkHistoryLoading}
                hasActiveCpis={(data?.activeCPIs.length ?? 0) > 0}
              />
            </RightTopRow>
            <CPIsSection
              key={categoryId}
              activeCPIs={data?.activeCPIs || []}
              availableCPIs={data?.availableCPIs || []}
              lastUpdate={data?.performance?.lastUpdate || ''}
              fillHeight={isEmptyState}
              loading={isLoading}
              availableLoading={isAvailableCPIsLoading}
              benchmarkingLoading={isBenchmarkingLoading}
              recentFindingLoading={isRecentFindingsLoading}
              activeTab={cpisTab}
              onTabChange={setCpisTab}
              onCpiActivated={async () => {
                await refetchActiveAndAvailable()
                setCpisTab('active')
              }}
            />
          </RightColumn>
        </DashboardContainer>
      </ContentWrapper>
    </>
  )
}

export default CategoryDashboard
